import {
  Accordion,
  AnchorButton,
  Button,
  H4,
  Text,
  TextColour,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  TProposalGrant,
  useGetProposal,
  useGetProposalFinancials,
} from '@/api/queries/proposal/useGetProposal';
import usePreferredNumberFormat from '@/hooks/usePreferredNumberFormat';
import AnimatedHeight from '@/ui/components/estimatesAndProposals/animatedHeight/AnimatedHeight';
import ProposalDiscountModal from '@/ui/pages/proposalBuilderPage/components/ProposalDiscountModal';
import { useHashFragment } from '@/utils/customHooks';

type ProposalFinancialsProps = {
  proposalId: string;
  projectId: string;
  /** Omit to show the material cost as plain text with no edit link. */
  editMaterialsTo?: string;
  /** Omit to show the labour cost as plain text with no edit link. */
  editLabourTo?: string;
  /** Pair with `onOpenChange` to persist the expanded state across remounts. */
  defaultOpen?: boolean;
  /** Called with the next expanded state whenever the summary is toggled. */
  onOpenChange?: (open: boolean) => void;
};

const DISCOUNT_MODAL_HASH = '#set-proposal-discount';

const SummaryStat: FC<{
  label: string;
  showDivider?: boolean;
  /** The value's colour, e.g. profit's positive/negative. */
  valueColour?: TextColour;
  children: ReactNode;
}> = (props) => {
  const { label, showDivider, valueColour, children } = props;

  return (
    <>
      {showDivider && (
        <span aria-hidden className="hidden @xl:block h-5 w-px bg-gray-300" />
      )}
      <span className="flex items-baseline justify-end gap-2">
        <Text as="span" level="supporting">
          {label}
        </Text>
        <Text as="span" colour={valueColour}>
          <strong>{children}</strong>
        </Text>
      </span>
    </>
  );
};

const GrantDeductionRow: FC<{ grant: TProposalGrant }> = (props) => {
  const { grant } = props;
  const labelId = `proposal-grant-${grant.id}`;

  return (
    <>
      <dt id={labelId} className="pb-1.5">
        <Text level="supporting">{grant.name}</Text>
      </dt>
      <dd aria-labelledby={labelId} className="text-right pb-1.5">
        <Text colour="positive">
          −<CurrencyValue {...grant.amount} />
        </Text>
      </dd>
    </>
  );
};

const ProposalFinancials: FC<ProposalFinancialsProps> = (props) => {
  const {
    proposalId,
    projectId,
    editMaterialsTo,
    editLabourTo,
    defaultOpen,
    onOpenChange,
  } = props;

  const { totals, grants, profitForecast, effectivePaymentSchedule } =
    useGetProposalFinancials(proposalId);
  const { proposal } = useGetProposal(proposalId);

  // The discount is only editable while the Proposal is editable — the backend
  // refuses to set one on a sent Proposal, so offering it here would only ever
  // produce an error. Same query as the financials above, so no extra fetch.
  const isSent = !!proposal?.sentAt;

  const [isDiscountModalOpen, toggleDiscountModal] =
    useHashFragment(DISCOUNT_MODAL_HASH);

  const percentFormat = usePreferredNumberFormat({
    style: 'percent',
    maximumFractionDigits: 2,
  });

  if (!totals) {
    return null;
  }

  const hasDiscount = totals.discount.value > 0;
  const hasCisDeduction = totals.cis.value > 0;
  const hasNegativeProfit = (profitForecast?.profit.value ?? 0) < 0;

  const attachedGrants = grants ?? [];
  const hasGrants = attachedGrants.length > 0;

  // Both a simple deposit and a schedule's deposit stage are normalised into the
  // effective schedule as one deposit stage. Grants reduce later stages
  // first, so the deposit is usually shown gross.
  const depositStage = effectivePaymentSchedule?.stages.find(
    (stage) =>
      stage.__typename === 'DerivedDepositStage' && stage.value.value > 0
  );
  const depositPercentageLabel =
    depositStage?.__typename === 'DerivedDepositStage' &&
    depositStage.shareOfTotal != null
      ? percentFormat.format(depositStage.shareOfTotal / 100)
      : null;

  return (
    <div className="@container px-3.5 py-5 border-b flex-none">
      <Accordion
        title="Financial summary"
        chevronPosition="left"
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        summary={
          <span className="@xl:flex flex-wrap items-center gap-3">
            <SummaryStat label={hasGrants ? 'Total payable:' : 'Total:'}>
              <CurrencyValue {...totals.totalPayable} />
            </SummaryStat>
            {profitForecast && (
              <SummaryStat
                label="Profit (ex tax):"
                showDivider
                valueColour={hasNegativeProfit ? 'negative' : 'positive'}
              >
                <CurrencyValue {...profitForecast.profit} />
              </SummaryStat>
            )}
          </span>
        }
      >
        <AnimatedHeight>
          <div className="mt-3 flex flex-col @xl:flex-row">
            <section className="flex flex-col flex-1 min-w-0 pb-4 @xl:pb-0 @xl:pr-6">
              <div className="mb-2">
                <H4>Totals</H4>
              </div>
              <dl className="grid grid-cols-2 items-end gap-y-1.5 m-0 [&>*]:m-0">
                <dt id="proposal-subtotal">
                  <Text level="supporting">Subtotal</Text>
                </dt>
                <dd aria-labelledby="proposal-subtotal" className="text-right">
                  <Text>
                    <CurrencyValue {...totals.subtotal} />
                  </Text>
                </dd>

                {(!isSent || hasDiscount) && (
                  <>
                    <dt id="proposal-discount">
                      <Text level="supporting">Discount</Text>
                    </dt>
                    <dd
                      aria-labelledby="proposal-discount"
                      className="flex items-center justify-end gap-2"
                    >
                      {isSent ? (
                        <Text>
                          −<CurrencyValue {...totals.discount} />
                        </Text>
                      ) : (
                        <Button variant="link" onClick={toggleDiscountModal}>
                          {hasDiscount ? (
                            <>
                              −<CurrencyValue {...totals.discount} />
                            </>
                          ) : (
                            'Add discount'
                          )}
                        </Button>
                      )}
                    </dd>
                  </>
                )}

                <dt id="proposal-tax" className="pb-1.5">
                  <Text level="supporting">Tax</Text>
                </dt>
                <dd
                  aria-labelledby="proposal-tax"
                  className="text-right pb-1.5"
                >
                  <Text>
                    <CurrencyValue {...totals.tax} />
                  </Text>
                </dd>

                {hasCisDeduction && (
                  <>
                    <dt id="proposal-cis" className="pb-1.5">
                      <Text level="supporting">CIS deduction</Text>
                    </dt>
                    <dd
                      aria-labelledby="proposal-cis"
                      className="text-right pb-1.5"
                    >
                      <Text>
                        −<CurrencyValue {...totals.cis} />
                      </Text>
                    </dd>
                  </>
                )}

                {depositStage && (
                  <>
                    <dt id="proposal-deposit" className="pb-1.5">
                      <Text level="supporting">Deposit due</Text>
                    </dt>
                    <dd
                      aria-labelledby="proposal-deposit"
                      className="text-right pb-1.5"
                    >
                      <Text>
                        <CurrencyValue {...depositStage.value} />
                        {depositPercentageLabel &&
                          ` (${depositPercentageLabel})`}
                      </Text>
                    </dd>
                  </>
                )}

                {hasGrants && (
                  <>
                    <dt id="proposal-total-before-grants" className="pb-1.5">
                      <Text level="supporting">Total before grants</Text>
                    </dt>
                    <dd
                      aria-labelledby="proposal-total-before-grants"
                      className="text-right pb-1.5"
                    >
                      <Text>
                        <CurrencyValue {...totals.total} />
                      </Text>
                    </dd>
                  </>
                )}

                {attachedGrants.map((grant) => (
                  <GrantDeductionRow key={grant.id} grant={grant} />
                ))}
              </dl>

              <dl className="mt-auto grid grid-cols-2 items-end m-0 [&>*]:m-0">
                <dt id="proposal-total" className="border-t pt-3.5">
                  <H4>{hasGrants ? 'Total payable' : 'Total'}</H4>
                </dt>
                <dd
                  aria-labelledby="proposal-total"
                  className="text-right border-t pt-3.5"
                >
                  <H4>
                    <CurrencyValue {...totals.totalPayable} />
                  </H4>
                </dd>
              </dl>
            </section>

            {profitForecast && (
              <section className="flex flex-col flex-1 min-w-0 border-t pt-4 @xl:border-t-0 @xl:pt-0 @xl:pl-6">
                <div className="mb-2">
                  <H4>Forecast</H4>
                </div>
                <dl className="grid grid-cols-2 items-end gap-y-1.5 m-0 [&>*]:m-0">
                  <dt id="proposal-material-cost">
                    <Text level="supporting">Material cost (ex tax)</Text>
                  </dt>
                  <dd
                    aria-labelledby="proposal-material-cost"
                    className="text-right"
                  >
                    {editMaterialsTo ? (
                      <AnchorButton
                        as={Link}
                        to={editMaterialsTo}
                        variant="link"
                        openInNewTab
                      >
                        <span className="sr-only">
                          Edit materials (opens in a new tab):{' '}
                        </span>
                        −<CurrencyValue {...profitForecast.materials} />
                      </AnchorButton>
                    ) : (
                      <Text>
                        −<CurrencyValue {...profitForecast.materials} />
                      </Text>
                    )}
                  </dd>

                  <dt id="proposal-labour-cost" className="pb-1.5">
                    <Text level="supporting">Labour cost (ex tax)</Text>
                  </dt>
                  <dd
                    aria-labelledby="proposal-labour-cost"
                    className="text-right pb-1.5"
                  >
                    {editLabourTo ? (
                      <AnchorButton
                        as={Link}
                        to={editLabourTo}
                        variant="link"
                        openInNewTab
                      >
                        <span className="sr-only">
                          Edit labour (opens in a new tab):{' '}
                        </span>
                        −<CurrencyValue {...profitForecast.labour} />
                      </AnchorButton>
                    ) : (
                      <Text>
                        −<CurrencyValue {...profitForecast.labour} />
                      </Text>
                    )}
                  </dd>
                </dl>

                <dl className="mt-auto grid grid-cols-2 items-end m-0 [&>*]:m-0">
                  <dt id="proposal-profit" className="border-t pt-3.5">
                    <H4>Profit (ex tax)</H4>
                  </dt>
                  <dd
                    aria-labelledby="proposal-profit"
                    className={clstx(
                      'text-right border-t pt-3.5',
                      hasNegativeProfit ? 'text-red-600' : 'text-green-600'
                    )}
                  >
                    <H4>
                      <CurrencyValue {...profitForecast.profit} />
                    </H4>
                  </dd>
                </dl>
              </section>
            )}
          </div>
        </AnimatedHeight>
      </Accordion>
      <ProposalDiscountModal
        isOpen={isDiscountModalOpen && !isSent}
        onClose={toggleDiscountModal}
        proposalId={proposalId}
        projectId={projectId}
        title={hasDiscount ? 'Edit discount' : 'Set discount'}
      />
    </div>
  );
};

export default ProposalFinancials;
